/**
 * @fileoverview Sorting the map's list (US-03 / F-E-07, 2026-09-28).
 *
 * **In the browser, over what is already loaded.** The nearby list carries
 * each place's distance, average rating, how many rated, visits and when it
 * was registered (one statement, OQ-35), so choosing an order asks for
 * nothing. What it orders is the list the API returned: the nearest 100
 * within 2km. When that list was cut at its limit, the heading says the
 * order is among the nearest ones (`countNote`) rather than fetching the
 * best-rated beyond them.
 *
 * Not remembered: every visit opens nearest first, like the purpose filter.
 */

export type PlaceSort = 'distance' | 'rating' | 'visits' | 'newest';

/** The choices, in the order the control lists them. */
export const PLACE_SORTS: readonly { value: PlaceSort; label: string }[] = [
  { value: 'distance', label: '近い順' },
  { value: 'rating', label: '評価の高い順' },
  { value: 'visits', label: '行った記録の多い順' },
  { value: 'newest', label: '新しく登録した順' },
];

export function isPlaceSort(value: string): value is PlaceSort {
  return PLACE_SORTS.some((sort) => sort.value === value);
}

/** What a place needs to be sorted in any of the orders. */
export interface SortablePlace {
  rating: { average: number | null; count: number };
  visitCount: number;
  createdAt: number;
}

type Compare = (a: SortablePlace, b: SortablePlace) => number;

/**
 * Highest average first, then more people who rated; places nobody rated
 * after every rated one.
 */
const betterRated: Compare = (a, b) => {
  const x = a.rating.average;
  const y = b.rating.average;
  if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
  return y - x || b.rating.count - a.rating.count;
};

const COMPARE: Record<Exclude<PlaceSort, 'distance'>, Compare> = {
  rating: betterRated,
  // No visits sorts last by itself: 0 is the fewest.
  visits: (a, b) => b.visitCount - a.visitCount,
  newest: (a, b) => b.createdAt - a.createdAt,
};

/**
 * `places` in the chosen order: a new array, the input left as it is.
 *
 * `places` is as the API gave it, nearest first — that is 「近い順」, kept
 * as it is. The other orders sort stably, so among equals (the same average
 * and count, as many visits, no rating at all) the nearer comes first.
 */
export function sortPlaces<T extends SortablePlace>(places: readonly T[], sort: PlaceSort): T[] {
  return sort === 'distance' ? [...places] : [...places].sort(COMPARE[sort]);
}

/**
 * The count in the list's heading. A list cut at its limit is the nearest
 * ones: sorted any other way, the heading says it is those it sorted, so
 * 「評価の高い順」 is not read as the best in the whole 2km.
 */
export function countNote(count: number, limit: number, sort: PlaceSort): string {
  if (count < limit) return `${count}件`;
  return sort === 'distance' ? `${count}件。近い順` : `近い${count}件の中で`;
}

/**
 * The row's 「★ 4.5（2人） ・ 行った記録 3件」, or `null` when there is
 * neither: no stars for a place nobody rated — an empty average is not a low
 * one.
 */
export function activitySummary(
  place: Pick<SortablePlace, 'rating' | 'visitCount'>,
): string | null {
  const parts: string[] = [];
  if (place.rating.average !== null) {
    parts.push(`★ ${place.rating.average.toFixed(1)}（${place.rating.count}人）`);
  }
  if (place.visitCount > 0) parts.push(`行った記録 ${place.visitCount}件`);
  return parts.length === 0 ? null : parts.join(' ・ ');
}
